import { AudioOutlined, CloseOutlined, FormOutlined, HistoryOutlined, StopOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, message, Skeleton, Tooltip } from 'antd'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Group, Panel, usePanelRef } from 'react-resizable-panels'
import noraLogo from '../../assets/nora-logo.png'
import { useAuth } from '../../contexts/AuthContext'
import { useBrowserDictation } from '../../hooks/useBrowserDictation'
import { useNoraChat } from '../../hooks/useNoraChat'
import { ContextChatPanel } from '../ContextChatPanel'
import type { NoraWidgetProps } from './NoraWidget.props'
import * as S from './style'

export function NoraWidget ({ initialPrompt, active = true, onClose }: NoraWidgetProps): React.ReactElement {
  const { user } = useAuth()
  const chat = useNoraChat(active, user, initialPrompt)
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches)
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(isMobile)
  const historyPanelRef = usePanelRef()
  const historyWidth = useRef(256)
  const dictation = useBrowserDictation(text => chat.setDraft(text.slice(0, 1000)), notice => { void message.error(notice) })

  const newConversation = (): void => {
    if (isMobile) historyPanelRef.current?.collapse()
    void chat.newConversation()
  }

  useEffect(() => {
    if (!active || chat.isSending) dictation.stop()
  }, [active, chat.isSending, dictation.stop])

  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)')
    const update = (): void => setIsMobile(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (isMobile) historyPanelRef.current?.collapse()
    else historyPanelRef.current?.resize(historyWidth.current)
  }, [isMobile, historyPanelRef])

  return <S.Root>
    <Group orientation={isMobile ? 'vertical' : 'horizontal'} className="nora-panels">
      <Panel panelRef={historyPanelRef} collapsible
        defaultSize={isMobile ? 0 : 256} collapsedSize={isMobile ? 0 : 56}
        minSize={isMobile ? 120 : 220} maxSize={isMobile ? 210 : '42%'}
        groupResizeBehavior="preserve-pixel-size"
        onResize={({ inPixels }) => {
          const collapsed = inPixels <= (isMobile ? 0 : 56) + 1
          setIsHistoryCollapsed(collapsed)
          if (!isMobile && !collapsed) historyWidth.current = inPixels
        }}>
        <S.History aria-label="Histórico de conversas" $collapsed={isHistoryCollapsed}>
          {isHistoryCollapsed
            ? <S.CollapsedActions>
              <Tooltip title="Nova conversa">
                <S.IconButton type="text" icon={<FormOutlined aria-hidden="true" />} aria-label="Nova conversa"
                  disabled={chat.isSending || chat.isLoading} onClick={newConversation} />
              </Tooltip>
              <Tooltip title="Ver histórico">
                <S.IconButton type="text" icon={<HistoryOutlined aria-hidden="true" />} aria-label="Expandir histórico"
                  onClick={() => historyPanelRef.current?.expand()} />
              </Tooltip>
            </S.CollapsedActions>
            : <>
              <S.HistoryHeader>
                <h2>Histórico</h2>
                <Tooltip title="Nova conversa">
                  <S.IconButton type="text" icon={<FormOutlined aria-hidden="true" />} aria-label="Nova conversa"
                    disabled={chat.isSending || chat.isLoading} onClick={newConversation} />
                </Tooltip>
              </S.HistoryHeader>
              <S.ConversationList>
                {chat.isLoading && !chat.conversations.length && <Skeleton active title={false} paragraph={{ rows: 3 }} />}
                {!chat.isLoading && !chat.conversations.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Suas conversas aparecerão aqui." />}
                {chat.conversations.map(conversation => <S.ConversationButton
                  key={conversation.id} $selected={conversation.id === chat.selectedId}
                  aria-current={conversation.id === chat.selectedId ? 'true' : undefined}
                  disabled={chat.isSending || chat.isLoading} title={conversation.title}
                  onClick={() => { if (isMobile) historyPanelRef.current?.collapse(); void chat.loadConversation(conversation.id) }}
          >
                  <span>{conversation.title}</span>
                  <time dateTime={conversation.updatedAt}>{new Date(conversation.updatedAt).toLocaleDateString('pt-BR')}</time>
                </S.ConversationButton>)}
                {chat.hasMoreConversations && <Button type="text" loading={chat.isLoadingOlder} disabled={chat.isSending || chat.isLoading}
                  onClick={() => { void chat.loadOlder('conversations') }}>Mais conversas</Button>}
              </S.ConversationList>
              <S.HistoryNotice>Seu histórico é privado nesta conta.</S.HistoryNotice>
            </>}
        </S.History>
      </Panel>
      <S.HistorySeparator aria-label="Redimensionar histórico" disabled={isMobile} $mobile={isMobile}>
        <S.ResizeGrip aria-hidden="true"><i /><i /><i /><i /></S.ResizeGrip>
      </S.HistorySeparator>
      <Panel minSize={isMobile ? '55%' : 320}>
        <S.ChatArea>
          {chat.error && <Alert type="error" showIcon message={chat.error}
            action={<Button size="small" disabled={chat.isSending || chat.isLoading} onClick={chat.reload}>Recarregar</Button>} />}
          {chat.hasMoreMessages && <Button type="link" loading={chat.isLoadingOlder}
            disabled={chat.isSending || chat.isLoading} onClick={() => { void chat.loadOlder('messages') }}>
            Carregar mensagens anteriores
          </Button>}
          <div data-chat-body>
            <ContextChatPanel
              avatarSrc={noraLogo}
              embedded showComposerContext={false} title="Assistente regulatória" dialogLabel="Conversa com a Nora" launcherLabel="Abrir Nora"
              emptyTitle="Como posso ajudar com sua dúvida sobre alimentos e bebidas?"
              placeholder="Pergunte à Nora..." messages={chat.messages} draft={chat.draft}
              suggestions={chat.suggestions} isSending={chat.isSending} isLoadingHistory={chat.isLoading}
              isInputDisabled={dictation.isListening} onDraftChange={chat.setDraft}
              onSend={question => { void chat.send(question) }} onStop={chat.stop}
              onReset={() => { void chat.newConversation() }}
              onCopy={() => {
                void navigator.clipboard.writeText(chat.messages.map(item => `${item.role === 'user' ? 'Você' : 'Nora'}: ${item.content}`).join('\n\n'))
                  .then(() => { void message.success('Conversa copiada.') })
                  .catch(() => { void message.error('Não foi possível copiar a conversa.') })
              }}
              notice="A Nora pode cometer erros. Confirme informações importantes nas fontes oficiais."
              headerActions={<>
                <Tooltip title={isHistoryCollapsed ? 'Ver histórico' : 'Ocultar histórico'}>
                  <S.IconButton className="mobile-action" type="text" icon={<HistoryOutlined aria-hidden="true" />}
                    aria-label="Histórico" aria-expanded={!isHistoryCollapsed}
                    onClick={() => {
                      if (isHistoryCollapsed) historyPanelRef.current?.expand()
                      else historyPanelRef.current?.collapse()
                    }} />
                </Tooltip>
                <Tooltip title="Nova conversa">
                  <S.IconButton className="mobile-action" type="text" icon={<FormOutlined aria-hidden="true" />} aria-label="Nova conversa"
                    disabled={chat.isSending || chat.isLoading} onClick={newConversation} />
                </Tooltip>
                {onClose && <Tooltip title="Fechar Nora"><S.IconButton type="text" icon={<CloseOutlined aria-hidden="true" />}
                  aria-label="Fechar Nora" onClick={onClose} /></Tooltip>}
              </>}
              composerControls={<Tooltip title={dictation.supported ? 'Ditar pergunta' : 'Ditado indisponível neste navegador'}>
                <S.IconButton type="text" icon={dictation.isListening ? <StopOutlined aria-hidden="true" /> : <AudioOutlined aria-hidden="true" />}
                  aria-label={dictation.isListening ? 'Parar ditado' : 'Ditar pergunta'}
                  aria-pressed={dictation.isListening} disabled={!dictation.supported || chat.isSending || chat.isLoading}
                  onClick={dictation.isListening ? dictation.stop : dictation.start} />
              </Tooltip>}
              renderMessage={item => <S.Markdown><ReactMarkdown
                allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'code', 'pre', 'br', 'a']}
                unwrapDisallowed components={{ a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a> }}
            >{item.content}</ReactMarkdown>
                {item.role === 'assistant' && Boolean(item.sources?.length) && <S.Sources aria-label="Fontes da ajuda">
                  {item.sources?.map(source => <li key={source.sourceId}>
                    <span>[{source.sourceId}] </span>
                    {source.sourceUrl
                      ? <a href={source.sourceUrl} title={source.title}>{source.heading.split(' / ').pop()}</a>
                      : source.heading.split(' / ').pop()}
                  </li>)}
                </S.Sources>}
              </S.Markdown>}
          />
          </div>
        </S.ChatArea>
      </Panel>
    </Group>
  </S.Root>
}
